import { ActivatedRoute, Router } from '@angular/router';
import {
    FEATURE_DYNAMIC_REPORTING,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    hasFeatureFlag,
    PersonsOrganisationDto
} from '@flyfreely-portal-ui/flyfreely';

/**
 * Depth first search from the router root to find the first node of the given type. There
 * should only be one in the tree.
 *
 * @param router the router instance
 * @param routeNodeType the type of route node to find
 * @returns
 */
export function findRelativeActivatedRoute(
    router: Router,
    routeNodeType = 'dashboard'
): ActivatedRoute | null {
    return findActivatedRoute(router.routerState.root, routeNodeType);
}

function findActivatedRoute(
    current: ActivatedRoute,
    routeNodeType: string
): ActivatedRoute | null {
    if (current.snapshot.data?.routeNodeType === routeNodeType) {
        return current;
    }
    for (const child of current.children) {
        const result = findActivatedRoute(child, routeNodeType);
        if (result != null) {
            return result;
        }
    }
    return null;
}

/**
 * An enum containing identifiers for each of the routable dashboards in the user app
 */
export enum DashboardSelectionOptions {
    MANAGE = 'MANAGE',
    MISSIONS = 'MISSIONS',
    REPORTING = 'REPORTING',
    TASKS = 'TASKS'
}

/**
 * A lookup object showing a displayable name for each of the dashboards in the user app
 */
export const dashboardSelectionDisplayableLabels = {
    [DashboardSelectionOptions.MANAGE]: 'Manage',
    [DashboardSelectionOptions.MISSIONS]: 'Missions',
    [DashboardSelectionOptions.REPORTING]: 'Reporting',
    [DashboardSelectionOptions.TASKS]: 'Tasks'
};

/**
 * A function that uses an organisation's feature flags to return a list of dashboards
 * available to that organisation. The list is sourced from the DashboardSelectionOptions enum.
 * @param organisation The organisation that will act as feature flag source, typically the currently loaded organisation.
 * @returns An array of DashboardSelectionOptions available to that organisation
 */
export function findAvailableDefaultDashboardsByOrganisationFeatureFlag(
    organisation: PersonsOrganisationDto
): DashboardSelectionOptions[] {
    const allowedOptions = [
        DashboardSelectionOptions.MISSIONS,
        DashboardSelectionOptions.MANAGE
    ];
    if (hasFeatureFlag(organisation, FEATURE_DYNAMIC_REPORTING)) {
        allowedOptions.push(DashboardSelectionOptions.REPORTING);
    }
    if (hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW)) {
        allowedOptions.push(DashboardSelectionOptions.TASKS);
    }
    return allowedOptions;
}
